import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

interface DeletedProject {
  id: number;
  project_name: string;
  deleted_at: string;
  user_id: number | null;
  tenant_id: number | null;
}

const DeletedProjectsBox: React.FC = () => {
  const [deletedProjects, setDeletedProjects] = useState<DeletedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);

  useEffect(() => {
    fetchDeletedProjects();
  }, []);

  const fetchDeletedProjects = async () => {
    try {
      const response = await api.get('/api/projects/deleted/list');
      setDeletedProjects(response.data.deletedProjects || []);
    } catch (error) {
      console.error('Failed to fetch deleted projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeAllDeletedProjects = async () => {
    setDeletingAll(true);

    try {
      const response = await api.delete('/api/projects/deleted/all');

      // Clear all projects from list
      setDeletedProjects([]);

      // Show success toast
      toast.success(response.data.message || 'All deleted projects removed successfully');
    } catch (error: any) {
      console.error('Error removing all deleted projects:', error);

      // Show error toast
      toast.error(error.response?.data?.error || 'Failed to remove deleted projects');
    } finally {
      setDeletingAll(false);
    }
  };

  const downloadPurgeCertificate = async (deletedProject: DeletedProject) => {
    setDownloading(deletedProject.id);

    try {
      const response = await api.post(`/api/projects/deleted/${deletedProject.id}/purge-certificate`, {}, {
        responseType: 'blob'
      });

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `purge-certificate-${deletedProject.project_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Remove from list after successful download
      setDeletedProjects(prev => prev.filter(p => p.id !== deletedProject.id));
    } catch (error) {
      console.error('Error downloading purge certificate:', error);
    } finally {
      setDownloading(null);
    }
  };

  // Don't render if no deleted projects
  if (loading || deletedProjects.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <button
            onClick={removeAllDeletedProjects}
            disabled={deletingAll}
            className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center hover:bg-red-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            title="Remove all deleted projects"
          >
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-red-800">
              🗑️ Deleted Projects
            </h3>
            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
              {deletedProjects.length} project{deletedProjects.length !== 1 ? 's' : ''}
            </span>
          </div>

          <p className="text-sm text-red-700 mb-4">
            Download your purge certificates to confirm complete project deletion.
            <span className="font-medium"> Projects will be removed from this list after download.</span>
          </p>

          <div className="space-y-3">
            {deletedProjects.map((project) => (
              <div key={project.id} className="bg-white border border-red-100 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="text-base font-medium text-gray-900">{project.project_name}</p>
                      <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                        DELETED
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadPurgeCertificate(project)}
                    disabled={downloading === project.id}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    <span>
                      {downloading === project.id ? 'Downloading...' : 'Download Certificate'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletedProjectsBox;